import { useEffect, useState } from 'react'
import { Trash2, Eye, Image as ImageIcon, Plus, Ban, CheckCircle } from 'lucide-react'
import { adminApi } from '../lib/api'
import PostFormModal from '../components/PostFormModal'
import Pagination from '../components/Pagination'
import type { Post } from '../types'
import { useToast } from '../components/ui/ToastProvider'

export default function PostsPage() {
  const { showToast } = useToast()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'text' | 'photo' | 'gallery'>('all')
  const [blockedFilter, setBlockedFilter] = useState<'all' | 'active' | 'blocked'>('all')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const itemsPerPage = 20

  const handlePostSuccess = () => {
    showToast('Post created successfully!', { type: 'success' })
    loadPosts()
  }

  useEffect(() => {
    loadPosts()
  }, [filter, blockedFilter, currentPage])

  const loadPosts = async () => {
    setLoading(true)
    try {
      const params: any = {
        limit: itemsPerPage,
        page: currentPage
      }
      if (filter !== 'all') {
        params.content_type = filter
      }
      if (blockedFilter === 'active') {
        params.is_blocked = false
      } else if (blockedFilter === 'blocked') {
        params.is_blocked = true
      }

      const response = await adminApi.getPosts(params)
      const postsData = response.data?.data?.posts || []
      const pagination = response.data?.data?.pagination || response.data?.pagination

      setPosts(postsData)

      // Update pagination info
      if (pagination) {
        setTotalPages(pagination.totalPages || Math.ceil(pagination.total / itemsPerPage) || 1)
        setTotalItems(pagination.total || postsData.length)
      } else {
        // If no pagination info, assume single page
        setTotalPages(1)
        setTotalItems(postsData.length)
      }
    } catch (error) {
      console.error('Failed to load posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return

    try {
      await adminApi.deletePost(id)
      loadPosts()
    } catch (error) {
      console.error('Failed to delete post:', error)
      showToast('Failed to delete post.', { type: 'error' })
    }
  }

  const handleBlock = async (id: string) => {
    try {
      await adminApi.blockPost(id)
      loadPosts()
    } catch (error) {
      console.error('Failed to block post:', error)
      showToast('Failed to block post.', { type: 'error' })
    }
  }

  const handleUnblock = async (id: string) => {
    try {
      await adminApi.unblockPost(id)
      loadPosts()
    } catch (error) {
      console.error('Failed to unblock post:', error)
      showToast('Failed to unblock post.', { type: 'error' })
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getContentTypeBadge = (type: string) => {
    const badges: Record<string, string> = {
      text: 'badge-secondary',
      photo: 'badge-info',
      gallery: 'badge-success',
      trip: 'badge-warning',
      event: 'badge-primary'
    }
    return badges[type] || 'badge-secondary'
  }

  const getTripProgress = (post: Post) => {
    if (!post.trip?.progress) return null
    const { daysElapsed, totalDays, photoCount, participantCount } = post.trip.progress
    return (
      <div className="text-xs text-gray-600 mt-1">
        {totalDays ? `${daysElapsed}/${totalDays} days` : `${daysElapsed} days`}
        {photoCount ? ` • ${photoCount} photos` : ''}
        {participantCount ? ` • ${participantCount} participants` : ''}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-600 mt-1">Manage user posts and content</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Post
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Content Type</label>
          <div className="flex gap-2">
            {['all', 'text', 'photo', 'gallery'].map((type) => (
              <button
                key={type}
                onClick={() => setFilter(type as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === type
                    ? 'text-black hover:opacity-90'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={filter === type ? { backgroundColor: '#97DF06' } : undefined}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All', icon: '📋' },
              { value: 'active', label: 'Active', icon: '✅' },
              { value: 'blocked', label: 'Blocked', icon: '🚫' }
            ].map((status) => (
              <button
                key={status.value}
                onClick={() => setBlockedFilter(status.value as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  blockedFilter === status.value
                    ? 'text-black hover:opacity-90'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={blockedFilter === status.value ? { backgroundColor: '#97DF06' } : undefined}
              >
                {status.icon} {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts List */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No posts found</div>
        ) : (
          <>
            <div className="divide-y">
              {posts.map((post) => (
              <div key={post.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex gap-4">
                  {/* Media Preview */}
                  {post.media.length > 0 && (
                    <div className="flex-shrink-0">
                      <div className="relative w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={post.media[0].url}
                          alt="Post media"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        {post.mediaCount > 1 && (
                          <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <ImageIcon className="h-3 w-3" />
                            {post.mediaCount}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* User Info */}
                        <div className="flex items-center gap-2 mb-2">
                          {post.user?.avatarUrl && (
                            <img
                              src={post.user.avatarUrl}
                              alt={post.user.displayName}
                              className="w-6 h-6 rounded-full"
                            />
                          )}
                          <span className="font-medium text-gray-900">
                            {post.user?.displayName || 'Unknown User'}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {formatDate(post.createdAt)}
                          </span>
                          <span className={`badge ${getContentTypeBadge(post.contentType)}`}>
                            {post.contentType}
                          </span>
                        </div>

                        {/* Description */}
                        {post.description && (
                          <p className="text-gray-700 mb-3 line-clamp-3">
                            {post.description}
                          </p>
                        )}

                        {/* Trip/Event Info */}
                        {post.trip && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-blue-900">
                                🗺️ {post.trip.title}
                              </span>
                              <span className={`badge ${
                                post.trip.status === 'active' ? 'badge-success' :
                                post.trip.status === 'planning' ? 'badge-warning' :
                                post.trip.status === 'completed' ? 'badge-info' :
                                'badge-secondary'
                              }`}>
                                {post.trip.status}
                              </span>
                            </div>
                            {getTripProgress(post)}
                          </div>
                        )}

                        {post.event && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                            <span className="text-sm font-medium text-purple-900">
                              📅 {post.event.title}
                            </span>
                            {post.event.startDate && (
                              <div className="text-xs text-purple-700 mt-1">
                                {formatDate(post.event.startDate)}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Location */}
                        {post.location && (
                          <div className="text-sm text-gray-600 flex items-center gap-1">
                            📍 {post.location.name}
                          </div>
                        )}

                        {/* Stats */}
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                          <span>❤️ {post.stats?.likes || 0} likes</span>
                          <span>💬 {post.stats?.comments || 0} comments</span>
                          <span className={`badge ${
                            post.privacy === 'public' ? 'badge-success' :
                            post.privacy === 'friends' ? 'badge-warning' :
                            'badge-secondary'
                          }`}>
                            {post.privacy}
                          </span>
                          {post.isBlocked && (
                            <span className="badge badge-danger">
                              🚫 Blocked
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {post.isBlocked ? (
                          <button
                            onClick={() => handleUnblock(post.id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded flex items-center gap-1"
                            title="Unblock"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlock(post.id)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded flex items-center gap-1"
                            title="Block"
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => window.open(`/posts/${post.id}`, '_blank')}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {/* Create Post Modal */}
      <PostFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handlePostSuccess}
      />
    </div>
  )
}
